import React, { useState, useEffect, useRef } from 'react';
import UploadForm from './components/UploadForm';
import Loading from './components/Loading';
import Report from './components/Report';
import LandingPage from './components/LandingPage';
import EmailPopup from './components/EmailPopup';
import axios from 'axios';
import testboxLogo from './assets/testbox-logo.svg';
import './App.css';

const API_BASE_URL = (process.env.REACT_APP_API_URL || 'http://localhost:8000').replace(/\/$/, '');
const DEFAULT_UPLOAD_CONTENT_TYPE = 'application/octet-stream';

function App() {
  const [currentView, setCurrentView] = useState('landing'); // 'landing', 'upload', 'loading', 'report'
  const [reportData, setReportData] = useState(null);
  const [, setError] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [progress, setProgress] = useState(0);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [showEmailPopup, setShowEmailPopup] = useState(false);
  const [pendingUpload, setPendingUpload] = useState(null); // Stores upload details after file is uploaded

  const timerRef = useRef(null);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  const startTimer = () => {
    setElapsedSeconds(0);
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    timerRef.current = setInterval(() => {
      setElapsedSeconds((prev) => prev + 1);
    }, 1000);
  };

  const stopTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const formatElapsedTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const requestSignedUploadUrl = async (file, submissionId) => {
    const uploadMeta = new FormData();
    uploadMeta.append('filename', file.name);
    uploadMeta.append('content_type', file.type || DEFAULT_UPLOAD_CONTENT_TYPE);
    uploadMeta.append('submission_id', submissionId);

    const response = await axios.post(`${API_BASE_URL}/api/generate-upload-url`, uploadMeta);
    return response.data;
  };

  const uploadFileToSignedUrl = async (signedUrl, file, contentType) => {
    await axios.put(signedUrl, file, {
      headers: { 'Content-Type': contentType },
    });
  };

  const submitAnalysisRequest = async (payload) => {
    const {
      gcsPath,
      originalFilename,
      formData,
      submissionId,
      notifyEmail = false,
    } = payload;

    const analysisForm = new FormData();
    analysisForm.append('gcs_path', gcsPath);
    analysisForm.append('original_filename', originalFilename);
    analysisForm.append('user_name', '');
    analysisForm.append('user_email', formData.userEmail || '');
    analysisForm.append('product_name', formData.productName);
    analysisForm.append('company_name', formData.companyName);
    analysisForm.append('target_audience', formData.targetAudience);
    analysisForm.append('submission_id', submissionId);
    analysisForm.append('notify_email', notifyEmail ? 'true' : 'false');

    return axios.post(`${API_BASE_URL}/api/analyze-gcs`, analysisForm, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 600000,
    });
  };

  const handleFormSubmit = async (formData) => {
    setCurrentView('loading');
    setError(null);
    setCurrentStep(0);
    setProgress(0);

    const submissionId =
      window.crypto?.randomUUID?.() ||
      `submission-${Date.now()}-${Math.random().toString(16).slice(2)}`;

    try {
      // Stage 0: Getting file ready (upload)
      setCurrentStep(0);
      setProgress(25);

      const uploadDetails = await requestSignedUploadUrl(formData.file, submissionId);
      await uploadFileToSignedUrl(uploadDetails.signed_url, formData.file, uploadDetails.content_type);

      // File uploaded - show email popup before analysis
      setPendingUpload({
        gcsPath: uploadDetails.gcs_path,
        originalFilename: formData.file.name,
        formData,
        submissionId,
      });
      setShowEmailPopup(true);
    } catch (err) {
      console.error('Error uploading file:', err);
      setError(err.response?.data?.detail || err.message || 'An error occurred during upload');
      setCurrentView('landing');
      alert(`Error: ${err.response?.data?.detail || err.message || 'An error occurred during upload'}`);
    }
  };

  // User chose to wait for the report (current sync flow)
  const handleWaitForReport = async () => {
    setShowEmailPopup(false);
    
    if (!pendingUpload) return;

    try {
      // Stage 1: Analysis (this takes ~4 minutes)
      setCurrentStep(1);
      setProgress(50);
      startTimer();

      const response = await submitAnalysisRequest(pendingUpload);

      stopTimer();

      // Stage 2: Preparing file (PDF generation - instant since it's in the response)
      setCurrentStep(2);
      setProgress(75);

      // Brief delay to show "Preparing file" stage
      await new Promise(resolve => setTimeout(resolve, 500));

      setProgress(100);
      setReportData(response.data);
      setCurrentView('report');
      setPendingUpload(null);
    } catch (err) {
      console.error('Error analyzing demo:', err);
      stopTimer();
      setError(err.response?.data?.detail || err.message || 'An error occurred during analysis');
      setCurrentView('landing');
      setPendingUpload(null);
      alert(`Error: ${err.response?.data?.detail || err.message || 'An error occurred during analysis'}`);
    }
  };

  // User chose to receive report via email
  const handleEmailMe = async (email) => {
    setShowEmailPopup(false);
    
    if (!pendingUpload) return;

    try {
      // Submit analysis with notify_email flag - returns immediately
      await submitAnalysisRequest({
        ...pendingUpload,
        formData: { ...pendingUpload.formData, userEmail: email },
        notifyEmail: true,
      });

      // Navigate to landing page
      setPendingUpload(null);
      setCurrentView('landing');
      alert('We\'ll email your report to ' + email + ' when it\'s ready!');
    } catch (err) {
      console.error('Error submitting analysis:', err);
      setError(err.response?.data?.detail || err.message || 'An error occurred');
      setCurrentView('landing');
      setPendingUpload(null);
      alert(`Error: ${err.response?.data?.detail || err.message || 'An error occurred'}`);
    }
  };

  const handleNewAnalysis = () => {
    setCurrentView('landing');
    setReportData(null);
    setError(null);
    setCurrentStep(0);
    setProgress(0);
    setElapsedSeconds(0);
    stopTimer();
  };

  return (
    <div className="App">
      {currentView !== 'landing' && <img src={testboxLogo} alt="TestBox" className="floating-logo" />}
      <main className="app-shell">
        {currentView === 'landing' && <LandingPage onStart={handleFormSubmit} />}
        {currentView === 'upload' && <UploadForm onSubmit={handleFormSubmit} />}
        {currentView === 'loading' && (
          <>
            <Loading
              currentStep={currentStep}
              progress={progress}
              elapsedTime={currentStep === 1 ? formatElapsedTime(elapsedSeconds) : null}
            />
            {showEmailPopup && (
              <EmailPopup
                email={pendingUpload?.formData?.userEmail || ''}
                onEmailMe={handleEmailMe}
                onWait={handleWaitForReport}
              />
            )}
          </>
        )}
        {currentView === 'report' && reportData && (
          <Report
            report={reportData.report}
            filename={reportData.filename}
            pdfBase64={reportData.pdf_base64}
            pdfFilename={reportData.pdf_filename}
            onNewAnalysis={handleNewAnalysis}
          />
        )}
      </main>
    </div>
  );
}

export default App;
